/**
 * Validation Schemas for FreshCards Subsystem
 * 
 * This module provides Zod schemas for validating FreshCards data structures
 * to ensure data integrity and type safety throughout the application.
 */

import { z } from 'zod';

// Common validation patterns
export const UUID_SCHEMA = z.string().uuid('Invalid UUID format');
export const NON_EMPTY_STRING = z.string().min(1, 'String cannot be empty');
export const POSITIVE_NUMBER = z.number().positive('Number must be positive');
export const DATE_SCHEMA = z.date();

// Metadata schemas
export const BlockMetadataSchema = z.object({
  aiEligible: z.boolean(),
  aiConfidence: z.number().min(0).max(1).optional(),
  lastAIProcessed: DATE_SCHEMA.optional()
});

export const PageMetadataSchema = z.object({
  createdAt: DATE_SCHEMA,
  updatedAt: DATE_SCHEMA,
  aiProcessed: z.boolean().optional()
});

// Block schemas
export const BlockSchema = z.object({
  id: z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i, 'Invalid block ID format'),
  type: NON_EMPTY_STRING,
  content: z.string().max(100000, 'Content exceeds maximum length (100,000 characters)').optional(),
  items: z.array(z.string().uuid('Invalid block ID format')).optional(),
  metadata: BlockMetadataSchema,
  phase: POSITIVE_NUMBER,
  version: z.string().min(1, 'Version cannot be empty')
});

export const BlockTypeSchema = z.object({
  type: NON_EMPTY_STRING,
  label: NON_EMPTY_STRING,
  icon: z.any(), // React component, can't be easily validated with Zod
  phase: POSITIVE_NUMBER,
  aiEligible: z.boolean(),
  defaultContent: z.any().optional()
});

// Page schemas
export const PageSchema = z.object({
  id: z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i, 'Invalid page ID format'),
  title: z.string().min(1, 'Title cannot be empty').max(500, 'Title exceeds maximum length (500 characters)'),
  icon: z.string().min(1, 'Icon cannot be empty').max(10, 'Icon exceeds maximum length (10 characters)'),
  parentId: UUID_SCHEMA.optional(),
  children: z.array(z.lazy(() => PageSchema)).optional(),
  metadata: PageMetadataSchema,
  phase: POSITIVE_NUMBER,
  version: z.string().min(1, 'Version cannot be empty')
});

// PageTree node schema
export const PageTreeNodeSchema = z.object({
  id: UUID_SCHEMA,
  title: NON_EMPTY_STRING,
  icon: z.string().min(1).max(10),
  parentId: UUID_SCHEMA.optional(),
  children: z.array(z.lazy(() => PageTreeNodeSchema)).optional(),
  metadata: PageMetadataSchema,
  phase: POSITIVE_NUMBER,
  version: NON_EMPTY_STRING,
  isExpanded: z.boolean().default(true),
  isSelected: z.boolean().default(false)
});

// Storage payload schemas
export const StoragePayloadSchema = z.object({
  pages: z.array(PageSchema),
  blocks: z.array(BlockSchema),
  metadata: z.object({
    exportedAt: DATE_SCHEMA,
    version: z.string(),
    totalCount: z.number()
  })
});

export const ImportPayloadSchema = z.object({
  pages: z.array(PageSchema).optional(),
  blocks: z.array(BlockSchema).optional(),
  metadata: z.object({
    importedAt: DATE_SCHEMA.optional(),
    version: z.string(),
    mergeMode: z.enum(['replace', 'merge']).default('merge')
  })
});

// Block content validation
export const BlockContentSchema = z.object({
  content: z.string().max(100000, 'Content exceeds maximum length (100,000 characters)'),
  isValid: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string())
});

// Page validation
export const PageValidationSchema = z.object({
  page: PageSchema,
  isValid: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  blocksCount: z.number().min(0)
});

// Block validation
export const BlockValidationSchema = z.object({
  block: BlockSchema,
  isValid: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string())
});

// Error schema for validation errors
export const ValidationErrorSchema = z.object({
  type: z.literal('validation'),
  field: z.string(),
  message: z.string(),
  value: z.any(),
  code: z.string()
});

// Storage error schema
export const StorageErrorSchema = z.object({
  type: z.literal('storage'),
  operation: z.enum(['save', 'load', 'delete', 'list']),
  message: z.string(),
  cause: z.string().optional(),
  timestamp: DATE_SCHEMA
});

// Import error schema
export const ImportErrorSchema = z.object({
  type: z.literal('import'),
  operation: z.enum(['import', 'export']),
  message: z.string(),
  details: z.string().optional(),
  timestamp: DATE_SCHEMA,
  validationErrors: z.array(ValidationErrorSchema).optional()
});

// Backup error schema
export const BackupErrorSchema = z.object({
  type: z.literal('backup'),
  operation: z.enum(['create', 'restore', 'validate']),
  message: z.string(),
  backupId: z.string().optional(),
  timestamp: DATE_SCHEMA,
  integrityCheck: z.boolean()
});

// General error schema
export const FreshCardsErrorSchema = z.discriminatedUnion('type', [
  ValidationErrorSchema,
  StorageErrorSchema,
  ImportErrorSchema,
  BackupErrorSchema
]);

// Form validation schemas
export const CreatePageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(500, 'Title is too long'),
  icon: z.string().min(1, 'Icon is required').max(10, 'Icon is too long'),
  parentId: UUID_SCHEMA.optional(),
  phase: POSITIVE_NUMBER.default(4)
});

export const UpdatePageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(500, 'Title is too long').optional(),
  icon: z.string().min(1, 'Icon is required').max(10, 'Icon is too long').optional(),
  metadata: PageMetadataSchema.partial().optional()
});

export const CreateBlockSchema = z.object({
  type: NON_EMPTY_STRING,
  content: z.string().max(100000, 'Content is too long').optional(),
  pageId: UUID_SCHEMA,
  phase: POSITIVE_NUMBER
});

export const UpdateBlockSchema = z.object({
  content: z.string().max(100000, 'Content is too long').optional(),
  metadata: BlockMetadataSchema.partial().optional(),
  phase: POSITIVE_NUMBER.optional()
});

// Export validation result schema
export const ValidationResultSchema = z.object({
  isValid: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  validatedItems: z.number(),
  skippedItems: z.number(),
  timestamp: DATE_SCHEMA
});

// Backup schema
export const BackupSchema = z.object({
  id: UUID_SCHEMA,
  name: z.string().min(1),
  description: z.string().optional(),
  createdAt: DATE_SCHEMA,
  version: z.string(),
  size: z.number(),
  checksum: z.string(),
  isValid: z.boolean(),
  metadata: z.object({
    totalPages: z.number(),
    totalBlocks: z.number(),
    exportFormat: z.literal('freshcards-v1'),
    compression: z.boolean()
  })
});

// Export all schemas for easy import
export const ValidationSchemas = {
  UUID: UUID_SCHEMA,
  NonEmptyString: NON_EMPTY_STRING,
  PositiveNumber: POSITIVE_NUMBER,
  Date: DATE_SCHEMA,
  
  BlockMetadata: BlockMetadataSchema,
  PageMetadata: PageMetadataSchema,
  
  Block: BlockSchema,
  BlockType: BlockTypeSchema,
  Page: PageSchema,
  PageTreeNode: PageTreeNodeSchema,
  
  StoragePayload: StoragePayloadSchema,
  ImportPayload: ImportPayloadSchema,
  
  BlockContent: BlockContentSchema,
  PageValidation: PageValidationSchema,
  BlockValidation: BlockValidationSchema,
  
  ValidationError: ValidationErrorSchema,
  StorageError: StorageErrorSchema,
  ImportError: ImportErrorSchema,
  BackupError: BackupErrorSchema,
  FreshCardsError: FreshCardsErrorSchema,
  
  CreatePage: CreatePageSchema,
  UpdatePage: UpdatePageSchema,
  CreateBlock: CreateBlockSchema,
  UpdateBlock: UpdateBlockSchema,
  
  ValidationResult: ValidationResultSchema,
  Backup: BackupSchema
};

// Type helpers for the schemas
export type Block = z.infer<typeof BlockSchema>;
export type Page = z.infer<typeof PageSchema>;
export type BlockMetadata = z.infer<typeof BlockMetadataSchema>;
export type PageMetadata = z.infer<typeof PageMetadataSchema>;
export type PageTreeNode = z.infer<typeof PageTreeNodeSchema>;
export type StoragePayload = z.infer<typeof StoragePayloadSchema>;
export type ImportPayload = z.infer<typeof ImportPayloadSchema>;
export type ValidationError = z.infer<typeof ValidationErrorSchema>;
export type StorageError = z.infer<typeof StorageErrorSchema>;
export type ImportError = z.infer<typeof ImportErrorSchema>;
export type BackupError = z.infer<typeof BackupErrorSchema>;
export type FreshCardsError = z.infer<typeof FreshCardsErrorSchema>;
export type ValidationResult = z.infer<typeof ValidationResultSchema>;
export type Backup = z.infer<typeof BackupSchema>;